import { 
    DEFAULT_CHATBOT_MESSAGES,
    DEFAULT_MESSAGE_CONFIG_OBJECT,
} from '@/common/constant/ChatBotMessageConfig';
import Logger from '@/common/utils/Logger';

/**
 * Validate message config structure
 * 
 * @param {Object} config - Parsed config object
 * @param {string} language - Language code to validate
 * @returns {boolean} - true if valid
 */
const validateConfig = (config, language) => {
    // Check if config is an object
    if (!config || typeof config !== 'object') {
        Logger.warn('MessageResolver: Config is not an object');
        return false;
    }

    // Check scope
    // if (!config?.scope || config?.scope !== 'KnowledgeRobo') {
    //     Logger.warn('MessageResolver: Invalid or missing scope', { scope: config?.scope });
    //     return false;
    // }

    // Check messages exists and is object
    if (!config?.messages || typeof config?.messages !== 'object') {
        Logger.warn('MessageResolver: Invalid messages structure');
        return false;
    }

    // Check if the specified language exists and is object
    if (!config?.messages?.[language] || typeof config?.messages[language] !== 'object') {
        Logger.warn('MessageResolver: Language messages not found', { language });
        return false;
    }

    return true;
};

/**
 * Resolve messages from config JSON
 * Validates structure and falls back to defaults when invalid
 * 
 * @param {string} messageConfigJson - JSON string from chatbotSetting.messageConfig
 * @returns {Object} - Resolved messages object with all keys
 */
export const resolveMessages = (messageConfigJson) => {
    // Handle null, undefined, or empty string
    if (!messageConfigJson || messageConfigJson.trim() === '') {
        Logger.info('MessageResolver: No config provided, using defaults');
        return { ...DEFAULT_CHATBOT_MESSAGES };
    }

    try {
        // Parse JSON
        const config = JSON.parse(messageConfigJson);

        // Validate config is an object
        if (!config || typeof config !== 'object') {
            Logger.warn('MessageResolver: Parsed config is not an object, using defaults');
            return { ...DEFAULT_CHATBOT_MESSAGES };
        }

        // Determine language to use
        // Priority: default_language from config -> 'ja' as fallback
        const DEFAULT_LANGUAGE = 'ja';

        // -> out of scope for now, always use 'ja' as default
        const language = config?.default_language || DEFAULT_LANGUAGE;
        
        Logger.info('MessageResolver: Using language', { language });

        // Validate structure with the determined language
        if (!validateConfig(config, language)) {
            // If validation fails, try fallback to DEFAULT_LANGUAGE
            if (language !== DEFAULT_LANGUAGE && validateConfig(config, DEFAULT_LANGUAGE)) {
                Logger.warn('MessageResolver: Specified language not found, falling back to Japanese');
                const jaMessages = config.messages?.ja;
                if (jaMessages && typeof jaMessages === 'object') {
                    return mergeWithDefaults(jaMessages);
                }
            }
            
            Logger.warn('MessageResolver: Invalid config structure, using defaults');
            return { ...DEFAULT_CHATBOT_MESSAGES };
        }

        // Extract messages for the determined language with null safety
        const languageMessages = config.messages?.[language];

        // Validate languageMessages before merging
        if (!languageMessages || typeof languageMessages !== 'object') {
            Logger.warn('MessageResolver: Language messages is invalid, using defaults');
            return { ...DEFAULT_CHATBOT_MESSAGES };
        }

        // Merge with defaults (per-key fallback)
        const resolved = mergeWithDefaults(languageMessages);

        Logger.info('MessageResolver: Successfully resolved messages from config', { language });
        return resolved;

    } catch (error) {
        Logger.error('MessageResolver: Failed to parse JSON', { error: error?.message, messageConfigJson });
        return { ...DEFAULT_CHATBOT_MESSAGES };
    }
};

/**
 * Merge language messages with defaults
 * 
 * @param {Object} languageMessages - Messages for specific language
 * @returns {Object} - Merged messages with defaults as fallback
 */
const mergeWithDefaults = (languageMessages) => {
    // Validate input
    if (!languageMessages || typeof languageMessages !== 'object') {
        Logger.warn('MessageResolver: Invalid languageMessages in mergeWithDefaults, using all defaults');
        return { ...DEFAULT_CHATBOT_MESSAGES };
    }

    const resolved = {};
    Object.keys(DEFAULT_CHATBOT_MESSAGES).forEach(key => {
        const customMessage = languageMessages?.[key];
        if (customMessage && typeof customMessage === 'string' && customMessage.trim() !== '') {
            resolved[key] = customMessage;
        } else {
            resolved[key] = DEFAULT_CHATBOT_MESSAGES[key];
        }
    });
    return resolved;
};

/**
 * Substitute variables in message text
 * Supports {{SelectedItem}}, {{totalKnowledge}}, and {{maxLength}}
 * 
 * @param {string} messageText - Text with {{variables}}
 * @param {Object} variables - {SelectedItem: "...", totalKnowledge: 5, maxLength: 200}
 * @returns {string} - Text with variables replaced
 */
export const substituteVariables = (messageText, variables = {}) => {
    if (!messageText || typeof messageText !== 'string') {
        return messageText;
    }

    let result = messageText;

    // Replace {{SelectedItem}}
    if (variables.SelectedItem !== undefined && variables.SelectedItem !== null) {
        result = result.replace(/\{\{SelectedItem\}\}/g, variables.SelectedItem);
    }

    // Replace {{totalKnowledge}}
    if (variables.totalKnowledge !== undefined && variables.totalKnowledge !== null) {
        result = result.replace(/\{\{totalKnowledge\}\}/g, variables.totalKnowledge);
    }

    // Replace {{maxLength}}
    if (variables.maxLength !== undefined && variables.maxLength !== null) {
        result = result.replace(/\{\{maxLength\}\}/g, variables.maxLength);
    }

    return result;
};

export const getDefaultMessageConfigJSON = (prettyPrint = true) => {
    return prettyPrint 
        ? JSON.stringify(DEFAULT_MESSAGE_CONFIG_OBJECT, null, 4) 
        : JSON.stringify(DEFAULT_MESSAGE_CONFIG_OBJECT);
};

/**
 * Get a specific message with variable substitution
 * Helper method for build functions in ChatBoxHandler
 * 
 * @param {Object} resolvedMessages - Messages object from resolveMessages()
 * @param {string} key - Message key to retrieve
 * @param {Object} variables - Variables for substitution
 * @returns {string} - Message text with variables substituted
 */
export const getMessageFromResolved = (resolvedMessages, key, variables = {}) => {
    // Validate resolvedMessages
    if (!resolvedMessages || typeof resolvedMessages !== 'object') {
        Logger.warn('MessageResolver: Invalid resolvedMessages, using default', { key });
        const defaultMessage = DEFAULT_CHATBOT_MESSAGES?.[key] || '';
        return substituteVariables(defaultMessage, variables);
    }

    // Get message from resolved or fallback to default
    const message = resolvedMessages?.[key] || DEFAULT_CHATBOT_MESSAGES?.[key] || '';
    
    if (!message) {
        Logger.warn('MessageResolver: Message key not found in resolved or defaults', { key });
        return '';
    }

    return substituteVariables(message, variables);
};
